// BTC3 Phase 2 · Experience Resolver client.
//
// Single authoritative accessor for post-authentication navigation.
// The helper NEVER silently sends the user to a workspace when the
// resolver call fails — consumers must present a visible error and
// offer retry.  Per Phase 2 verification §4: "A resolver failure
// must never silently place the user into a workspace."
//
// Contract mirrors backend/experience_resolver.py:
//   { identity, participations, roles, lifecycle_state,
//     holding_state, capabilities, primary_workspace,
//     landing_route, next_action }

import { api } from "./auth";

/**
 * Fetch the full resolver payload for the authenticated user.
 * Throws on any network / server / contract failure.
 */
export async function fetchExperience() {
  const { data } = await api.get("/experience/resolve");
  if (!data || typeof data !== "object" || !data.landing_route) {
    const err = new Error("EXPERIENCE_RESOLVER_CONTRACT_INVALID");
    err.code = "EXPERIENCE_RESOLVER_CONTRACT_INVALID";
    throw err;
  }
  return data;
}

/**
 * Return the canonical landing route for the authenticated user.
 * Throws when the resolver call or its contract fails — consumers
 * MUST catch this and render a visible failure state.  There is no
 * silent workspace fallback.
 */
export async function resolveLandingRoute() {
  const exp = await fetchExperience();
  return exp.landing_route;
}

/**
 * BTC3 Foundation Verification · Step 3.4 — deep-link "from" parameter
 * hardening.
 *
 * Login accepts a `location.state.from` hint so an unauthenticated
 * user landing on a protected route can be returned there after
 * authentication.  Only relative internal paths beginning with a
 * single "/" are accepted.  Everything else routes through the
 * resolver (i.e. `from` is discarded).
 *
 * Rejected inputs:
 *   - absolute URLs   (http://, https://, ftp://, javascript:, etc.)
 *   - protocol-relative URLs starting with "//"
 *   - backslash variants ("\\path", "\\\\attacker.example")
 *   - empty / non-string inputs
 *   - URLs that would leave the app (anything with ":" in the scheme
 *     position or "@" indicating userinfo)
 *
 * Returns the safe path when accepted, or `null` when rejected.
 */
export function safeInternalPath(candidate) {
  if (typeof candidate !== "string" || candidate.length === 0) return null;
  // Must begin with exactly one "/" and not "//" (protocol-relative).
  if (candidate[0] !== "/") return null;
  if (candidate.length >= 2 && candidate[1] === "/") return null;
  // Reject backslash variants ("\..." or "/\..." which some browsers
  // normalise to "//...").
  if (candidate.includes("\\")) return null;
  // Reject protocol/userinfo in the pathname portion.
  //   "/http://…" and "/user@host/…" and "/javascript:alert(1)" all
  //   caught by these two predicates.
  if (candidate.includes("://")) return null;
  return candidate;
}
